import React from 'react'
import { Flex, Tooltip } from 'antd'
import columns, { ColumnTitleWithTooltip } from 'UI/VerticalTable/columns'
import { COLUMNS_IDS } from 'UI/VerticalTable/utils'
import { EllipsisCell, ITableColumnsType } from 'uikit/tables/default'
import { removeKeywordSuffix } from 'containers/VerticalPage/common/utils'
import { NOT_AVAILABLE } from 'utils/constants'
import Income from 'components/Income'
import { PlatformIconLink } from 'containers/VerticalPage/str/components'
import type { STREntityResponse } from 'containers/VerticalPage/str/types'
import { ESTIMATED_REVENUE_MIN_MULTIPLIER, ESTIMATED_REVENUE_MAX_MULTIPLIER } from 'containers/VerticalPage/str/utils'

const MAX_VISIBLE_HOST_ICONS = 5

const OVERFLOW_BADGE_STYLE: React.CSSProperties = {
  width: 20,
  height: 20,
  borderRadius: '50%',
  background: '#f0f0f0',
  color: '#595959',
  fontSize: 11,
  fontWeight: 500,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'default',
}

const HostProfileCell = (item: STREntityResponse) => {
  const hosts = item?.overviewDetails?.hosts ?? []
  if (!hosts.length) return NOT_AVAILABLE

  const visibleHosts = hosts.slice(0, MAX_VISIBLE_HOST_ICONS)
  const overflowHosts = hosts.slice(MAX_VISIBLE_HOST_ICONS)

  return (
    <Flex gap={6} align='center'>
      {visibleHosts.map(host => (
        <PlatformIconLink
          key={`${host.platform}-${host.url}`}
          platform={host.platform}
          url={host.url}
          name={host.name}
        />
      ))}
      {overflowHosts.length > 0 && (
        <Tooltip
          mouseLeaveDelay={0.3}
          title={
            <Flex gap={6} align='center' wrap='wrap'>
              {overflowHosts.map(host => (
                <PlatformIconLink
                  key={`${host.platform}-${host.url}`}
                  platform={host.platform}
                  url={host.url}
                  name={host.name}
                />
              ))}
            </Flex>
          }
        >
          <span style={OVERFLOW_BADGE_STYLE} onClick={e => e.stopPropagation()}>
            +{overflowHosts.length}
          </span>
        </Tooltip>
      )}
    </Flex>
  )
}

export const STR_COLUMN_KEYS = {
  taxpayerType: 'overviewDetails.activityType',
  reportedRevenue: 'overviewDetails.reportedRevenue',
}

export const strColumns: ITableColumnsType<any> = [
  { ...columns[COLUMNS_IDS.tin], title: 'TIN', fixed: 'left' },
  {
    ...columns[COLUMNS_IDS.profileNames],
    fixed: false,
    title: <ColumnTitleWithTooltip title='Host Profile' tooltipText='Profile names used by the host online' />,
    minWidth: 160,
    render: HostProfileCell,
  },
  {
    ...columns[COLUMNS_IDS.hostName],
    title: (
      <ColumnTitleWithTooltip
        title='Verified Name'
        tooltipText='Full name of the person or company that holds the license to operate the STR unit at this address.'
      />
    ),
    minWidth: 150,
    key: 'entityDetails.name',
    render: (item: STREntityResponse) => item?.entityDetails?.name || NOT_AVAILABLE,
  },
  { ...columns[COLUMNS_IDS.unreportedRevenue], title: 'Unreported Revenue', minWidth: 180 },
  {
    ...columns[COLUMNS_IDS.ecommerceEstimatedRevenue],
    title: (
      <ColumnTitleWithTooltip
        title='Estimated Revenue'
        tooltipText='The estimated revenue range is based on IVIX’s calculations and is not self reported by the host or by Airbnb.'
      />
    ),
    width: 240,
    minWidth: 220,
    render: (item: STREntityResponse): React.ReactNode => {
      const raw = item?.totals?.estimatedRevenue
      if (!raw) return NOT_AVAILABLE
      return (
        <span>
          <Income value={raw * ESTIMATED_REVENUE_MIN_MULTIPLIER} useCurrencyConversion />
          {' - '}
          <Income value={raw * ESTIMATED_REVENUE_MAX_MULTIPLIER} useCurrencyConversion />
        </span>
      )
    },
  },
  {
    ...columns[COLUMNS_IDS.reported],
    title: 'Reported Revenue',
    minWidth: 220,
  },
  {
    title: 'Taxpayer Type',
    width: 150,
    minWidth: 150,
    sorter: true,
    key: STR_COLUMN_KEYS.taxpayerType,
    render: (item: any) => item?.overviewDetails?.activityType || NOT_AVAILABLE,
  },
  {
    ...columns[COLUMNS_IDS.addresses],
    title: 'Host Location',
    minWidth: 140,
    render: (item: STREntityResponse) => {
      const addresses = item?.contactInfo?.addresses
      if (!Array.isArray(addresses) || addresses.length === 0) return NOT_AVAILABLE

      const overflowCount = addresses.length - 1
      const display = overflowCount > 0 ? `${addresses[0]} +${overflowCount}` : addresses[0]
      const tooltipText = overflowCount > 0 ? addresses.join(', ') : undefined

      return (
        <EllipsisCell maxLines={2} tooltipText={tooltipText}>
          {display}
        </EllipsisCell>
      )
    },
  },
  { ...columns[COLUMNS_IDS.activeListings], width: 175, minWidth: 170 },
  { ...columns[COLUMNS_IDS.totalListings], width: 175, minWidth: 170 },
  {
    ...columns[COLUMNS_IDS.ecommerceTotalReviews],
    title: (
      <ColumnTitleWithTooltip
        title='Number Of Reviews'
        tooltipText='Total number of reviews across all of the host’s listings.'
      />
    ),
    width: 195,
    minWidth: 190,
  },
  { ...columns[COLUMNS_IDS.caseId], title: 'Case ID' },
]

export const strDefaultVisibleColumns = [
  COLUMNS_IDS.profileNames,
  'entityDetails.name',
  COLUMNS_IDS.tin,
  STR_COLUMN_KEYS.taxpayerType,
  COLUMNS_IDS.addresses,
  COLUMNS_IDS.unreportedRevenue,
]

export const keysForEmptyColumnsRequest = removeKeywordSuffix(
  strColumns.map(el => `${el.key}` || '').filter(key => Boolean(key)),
)
